import { Helmet } from "react-helmet-async";
import { FACTS_APPROVED } from "../companyFacts";
import { isTempNoindex } from "../seoPolicy";

const ORIGIN = "https://sourcingindia.org";

/**
 * Central SEO head manager: title, meta description, canonical, robots,
 * Open Graph, Twitter and JSON-LD. Used on every route.
 */
export default function Seo({ title, description, path = "/", type = "website", jsonLd = [], noindex = false, region = null, image = null, imageAlt = null }) {
  const canonical = ORIGIN + path;
  const img = image ? (image.startsWith("http") ? image : ORIGIN + image) : ORIGIN + "/assets/og-sourcing-india.jpg";
  const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
  const temp = !noindex && isTempNoindex(path);
  const robots = noindex ? "noindex,nofollow" : temp ? "noindex,follow" : "index,follow,max-image-preview:large";
  const alt = !noindex && !temp;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={robots} />
      {alt && <link rel="alternate" hrefLang="en" href={canonical} />}
      {alt && region && <link rel="alternate" hrefLang={"en-" + region} href={canonical} />}
      {alt && <link rel="alternate" hrefLang="x-default" href={canonical} />}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Sourcing India" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={img} />
      {!image && <meta property="og:image:width" content="1200" />}
      {!image && <meta property="og:image:height" content="630" />}
      <meta property="og:image:alt" content={imageAlt || title} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {blocks.filter(Boolean).map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>
      ))}
    </Helmet>
  );
}

export const ORIGIN_URL = ORIGIN;

/* ---- shared JSON-LD builders ---- */
export const orgLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Sourcing India",
  url: ORIGIN + "/",
  description: "India sourcing company and buying agent helping international buyers source from verified Indian manufacturers.",
  ...(FACTS_APPROVED.contact ? { email: "sam@creativeconceptsindia.com", telephone: "+91-93684-37527" } : {}),
  ...(FACTS_APPROVED.address ? { address: {
    "@type": "PostalAddress",
    streetAddress: "D-8, Manarover Colony, Delhi Road",
    addressLocality: "Moradabad",
    addressRegion: "Uttar Pradesh",
    postalCode: "244001",
    addressCountry: "IN",
  } } : {}),
  ...(FACTS_APPROVED.founderTitle ? { founder: { "@type": "Person", name: "Dr. Sumit Chhabra", jobTitle: "Founder & CEO" } } : {}),
  knowsAbout: [
    "India sourcing", "garment manufacturers India", "home decor manufacturers India",
    "furniture manufacturers India", "carpet manufacturers India", "home furnishing India",
    "stainless steel utensils India", "cutlery manufacturers India", "plastic products India",
    "handicraft manufacturers India", "leather products India", "glassware India", "woodware India",
    "essential oils India", "incense and attars India", "imitation jewellery India", "factory audit", "quality control",
  ],
};

export const localBusinessLd = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": ORIGIN + "/#office",
  name: "Sourcing India",
  url: ORIGIN + "/",
  image: ORIGIN + "/assets/og-sourcing-india.jpg",
  description: "Buyer-side sourcing, factory verification and quality control in India for international brands, retailers and importers.",
  telephone: "+91-93684-37527",
  email: "sam@creativeconceptsindia.com",
  address: orgLd.address,
  areaServed: "Worldwide",
  parentOrganization: { "@type": "Organization", name: "Sourcing India", url: ORIGIN + "/" },
};

export const websiteLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Sourcing India",
  url: ORIGIN + "/",
};

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: ORIGIN + it.path,
  })),
});

export const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const serviceLd = ({ name, description, path }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: name,
  name,
  description,
  url: ORIGIN + path,
  provider: { "@type": "Organization", name: "Sourcing India", url: ORIGIN + "/" },
});

export const articleLd = ({ title, description, path }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  url: ORIGIN + path,
  mainEntityOfPage: ORIGIN + path,
  author: { "@type": "Organization", name: "Sourcing India", url: ORIGIN + "/" },
  publisher: { "@type": "Organization", name: "Sourcing India", url: ORIGIN + "/" },
});

export const itemListLd = (name, items) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name,
  itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it })),
});

export const personLd = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Dr. Sumit Chhabra",
  jobTitle: "Founder & CEO",
  worksFor: { "@type": "Organization", name: "Sourcing India", url: ORIGIN + "/" },
  image: ORIGIN + "/assets/dr-sumit-chhabra-founder-sourcing-india.webp",
};
